<template>
  <div class="panel panel-default">

    <!-- Correct answer banner -->
    <transition name="fade">
      <div v-if="answered" class="feedback feedback-correct text-center">
        <span class="feedback-text">✓ Correct! Well done!</span>
        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
        </div>
      </div>
    </transition>

    <!-- Wrong answer banner -->
    <transition name="fade">
      <div v-if="wrongAnswer" class="feedback feedback-wrong text-center">
        <span class="feedback-text">✗ Wrong! Try again.</span>
      </div>
    </transition>

    <div class="panel-heading">
      <p class="seq-type-label text-center">{{ seqTypeLabel }}</p>
      <h3 class="panel-title text-center">What is the missing number?</h3>
      <h2 class="text-center sequence-display">{{ sequenceDisplay }}</h2>
      <p v-if="hintText" class="hint-text text-center">{{ hintText }}</p>
    </div>

    <!-- Answer buttons -->
    <div class="panel-body answer-grid">
      <button
        v-for="(btn, index) in btnData"
        :key="index"
        class="ans-btn"
        :disabled="answered"
        @click="onAnswer(btn.correct)"
      >
        {{ btn.answer | formatLarge }}
      </button>
    </div>

    <!-- Next Question -->
    <div class="text-center" style="margin-top: 16px; margin-bottom: 8px;">
      <button class="next-btn" @click="nextQuestion">Next Question →</button>
    </div>
  </div>
</template>

<script>
// ─── Helpers ──────────────────────────────────────────────────────────────

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ─── Prime helpers ────────────────────────────────────────────────────────

// Sieve of Eratosthenes up to 10 000 - covers all prime sequences we need
const PRIMES = (function () {
  const limit = 10000;
  const sieve = new Uint8Array(limit + 1).fill(1);
  sieve[0] = sieve[1] = 0;
  for (let i = 2; i * i <= limit; i++) {
    if (sieve[i]) for (let j = i * i; j <= limit; j += i) sieve[j] = 0;
  }
  const out = [];
  for (let i = 2; i <= limit; i++) if (sieve[i]) out.push(i);
  return out;
})();

function isPrime(n) {
  if (n < 2) return false;
  if (n <= 10000) return PRIMES.includes(n); // fast for small
  for (let i = 2; i <= Math.sqrt(n); i++) if (n % i === 0) return false;
  return true;
}

// ─── Sequence generators ─────────────────────────────────────────────────

/**
 * Arithmetic: a, a+d, a+2d, ...
 * Advanced option: negative step (decreasing) and larger starting values.
 */
function makeArithmetic() {
  const advanced = Math.random() < 0.4; // 40% chance of harder version
  let start, step, length;

  if (advanced) {
    // larger numbers, possible negative step
    start  = randInt(50, 5000);
    step   = randInt(1, 500) * (Math.random() < 0.35 ? -1 : 1);
    length = 6;
  } else {
    start  = randInt(1, 30);
    step   = randInt(1, 20);
    length = 5;
  }

  const seq = [];
  for (let i = 0; i < length; i++) seq.push(start + step * i);

  // ensure no negatives in the visible portion for basic mode
  if (!advanced && seq.some(n => n <= 0)) return makeArithmetic();

  const hiddenIdx = randInt(0, seq.length - 1);
  const correct   = seq[hiddenIdx];

  // wrong answers: offsets scaled to the step size
  const scale    = Math.max(1, Math.abs(step));
  const offsets  = new Set();
  while (offsets.size < 3) {
    const off = randInt(1, 4) * scale * (Math.random() > 0.5 ? 1 : -1);
    const w   = correct + off;
    if (w !== correct) offsets.add(w);
  }

  const options = shuffle([
    { correct: true,  answer: correct },
    ...[...offsets].map(w => ({ correct: false, answer: w })),
  ]);

  const stepStr = step >= 0 ? `+${step}` : `${step}`;
  return {
    seq, hiddenIdx,
    options,
    typeLabel: "Arithmetic sequence",
    hint: `Rule: ${stepStr} each term`,
  };
}

/**
 * Geometric: a, a·r, a·r², ...
 * Supports large ratios (up to 10) and large starting values.
 */
function makeGeometric() {
  const hard  = Math.random() < 0.45;
  const start = hard ? randInt(2, 20)  : randInt(1, 10);
  const ratio = hard ? randInt(4, 10)  : randInt(2, 4);
  const length = 5;

  const seq = [];
  for (let i = 0; i < length; i++) seq.push(start * Math.pow(ratio, i));

  const hiddenIdx = randInt(0, seq.length - 1);
  const correct   = seq[hiddenIdx];

  // wrong answers: neighbouring terms or ratio-adjacent values
  const candidates = new Set();
  while (candidates.size < 3) {
    const type = randInt(0, 2);
    let w;
    if (type === 0) {
      // adjacent multiplier variant
      w = Math.round(correct * (ratio + randInt(1, 3) * (Math.random() > 0.5 ? 1 : -1)));
    } else if (type === 1) {
      w = correct + randInt(1, Math.max(2, Math.round(correct * 0.1)));
    } else {
      w = Math.round(correct / ratio) * (ratio + 1);
    }
    if (w > 0 && w !== correct) candidates.add(w);
  }

  const options = shuffle([
    { correct: true,  answer: correct },
    ...[...candidates].map(w => ({ correct: false, answer: w })),
  ]);

  return {
    seq, hiddenIdx,
    options,
    typeLabel: "Geometric sequence",
    hint: `Rule: ×${ratio} each term`,
  };
}

/**
 * Fibonacci-style: each term = sum of previous two.
 * Start with random seeds to avoid always showing the canonical sequence.
 */
function makeFibonacci() {
  const hard = Math.random() < 0.4;
  let a, b;

  if (hard) {
    // Start mid-sequence with larger seeds
    a = randInt(5, 50);
    b = randInt(a, a * 3);
  } else {
    a = randInt(1, 5);
    b = randInt(a, a + 8);
  }

  const length = 7; // more terms = more context
  const seq    = [a, b];
  for (let i = 2; i < length; i++) seq.push(seq[i - 1] + seq[i - 2]);

  const hiddenIdx = randInt(2, seq.length - 1); // never hide the first two seeds
  const correct   = seq[hiddenIdx];

  // wrong answers: nearby plausible Fibonacci-like mistakes
  const scale     = Math.max(1, Math.round(correct * 0.08));
  const candidates = new Set();
  while (candidates.size < 3) {
    const off = randInt(1, 5) * scale * (Math.random() > 0.5 ? 1 : -1);
    const w   = correct + off;
    if (w > 0 && w !== correct) candidates.add(w);
  }

  const options = shuffle([
    { correct: true,  answer: correct },
    ...[...candidates].map(w => ({ correct: false, answer: w })),
  ]);

  return {
    seq, hiddenIdx,
    options,
    typeLabel: "Fibonacci-style sequence",
    hint: "Rule: each term = sum of the two before it",
  };
}

/**
 * Prime numbers: consecutive primes starting from a random index.
 * Ranges from small (2,3,5,7,11...) to large primes (4000s+).
 */
function makePrime() {
  const hard       = Math.random() < 0.45;
  const maxStart   = hard ? PRIMES.length - 8 : Math.min(30, PRIMES.length - 8);
  const startIdx   = randInt(0, maxStart);
  const length     = 6;
  const seq        = PRIMES.slice(startIdx, startIdx + length);

  const hiddenIdx = randInt(0, seq.length - 1);
  const correct   = seq[hiddenIdx];

  // wrong answers: nearby primes or composites that look plausible
  const candidates = new Set();
  const pool = [
    ...PRIMES.slice(Math.max(0, startIdx - 4), startIdx + length + 4),
  ].filter(p => p !== correct);

  // pick from nearby primes first, then pad with composites
  while (candidates.size < 3 && pool.length > 0) {
    const pick = pool.splice(randInt(0, pool.length - 1), 1)[0];
    candidates.add(pick);
  }
  // fallback: composite numbers near the correct answer
  while (candidates.size < 3) {
    const off = randInt(2, 6) * (Math.random() > 0.5 ? 1 : -1);
    const w   = correct + off;
    if (w > 1 && !isPrime(w) && w !== correct) candidates.add(w);
  }

  const options = shuffle([
    { correct: true,  answer: correct },
    ...[...candidates].map(w => ({ correct: false, answer: w })),
  ]);

  return {
    seq, hiddenIdx,
    options,
    typeLabel: "Prime number sequence",
    hint: "These are consecutive prime numbers",
  };
}

// ─── Component ────────────────────────────────────────────────────────────
export default {
  filters: {
    // Format large numbers with comma separators for readability
    formatLarge(n) {
      if (n === undefined || n === null) return "";
      return Number(n).toLocaleString();
    },
  },

  props: {
    category: {
      type: String,
      default: "all",
    },
  },

  data() {
    return {
      answered:        false,
      wrongAnswer:     false,
      progressWidth:   0,
      sequenceDisplay: "",
      seqTypeLabel:    "",
      hintText:        "",
      btnData:         [],
      _progressTimer:  null,
      _autoTimer:      null,
    };
  },

  methods: {
    pickGenerator() {
      if (this.category === "arithmetic") return makeArithmetic;
      if (this.category === "geometric")  return makeGeometric;
      if (this.category === "fibonacci")  return makeFibonacci;
      if (this.category === "prime")      return makePrime;
      // "all" - weighted pool: arithmetic 30%, geometric 25%, fibonacci 25%, prime 20%
      const roll = Math.random();
      if (roll < 0.30) return makeArithmetic;
      if (roll < 0.55) return makeGeometric;
      if (roll < 0.80) return makeFibonacci;
      return makePrime;
    },

    generateQuestion() {
      this.answered      = false;
      this.wrongAnswer   = false;
      this.progressWidth = 0;
      clearTimeout(this._autoTimer);
      clearInterval(this._progressTimer);

      const gen = this.pickGenerator();
      const { seq, hiddenIdx, options, typeLabel, hint } = gen();

      this.seqTypeLabel    = typeLabel;
      this.hintText        = "";   // only shown after correct answer
      this._pendingHint    = hint; // stored to reveal after answering

      this.sequenceDisplay = seq
        .map((n, i) => i === hiddenIdx ? "?" : Number(n).toLocaleString())
        .join("  ,  ");

      this.btnData = options;
    },

    onAnswer(isCorrect) {
      if (isCorrect) {
        this.wrongAnswer   = false;
        this.answered      = true;
        this.hintText      = this._pendingHint; // reveal the rule
        this.progressWidth = 0;
        const duration = 3000;
        const interval = 30;
        const step     = (interval / duration) * 100;
        this._progressTimer = setInterval(() => {
          this.progressWidth = Math.min(this.progressWidth + step, 100);
        }, interval);
        this._autoTimer = setTimeout(() => {
          clearInterval(this._progressTimer);
          this.generateQuestion();
          this.$emit("answered");
        }, duration);
      } else {
        this.wrongAnswer = true;
        this.$emit("answered", false);
      }
    },

    nextQuestion() {
      clearInterval(this._progressTimer);
      clearTimeout(this._autoTimer);
      this.generateQuestion();
      this.$emit("confirmed");
    },
  },

  created() {
    this.generateQuestion();
  },
};
</script>

<style scoped>
/* ── Feedback ─────────────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 17px;
  font-weight: bold;
}
.feedback-correct {
  background: rgba(3, 120, 60, 0.12);
  color: rgb(3, 100, 50);
  border: 2px solid rgba(3, 120, 60, 0.3);
  flex-direction: column;
  gap: 10px;
}
.progress-bar-wrap {
  width: 100%;
  height: 6px;
  background: rgba(3, 120, 60, 0.2);
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: rgb(3, 120, 60);
  border-radius: 4px;
  transition: width 30ms linear;
}
.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 2px solid rgba(180, 30, 30, 0.3);
}
.feedback-text { font-size: 1.1rem; }

/* ── Sequence type label ─────────────────────────────────────────── */
.seq-type-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(3, 53, 48, 0.55);
  margin-bottom: 2px;
}

/* ── Sequence display ─────────────────────────────────────────────── */
.sequence-display {
  letter-spacing: 3px;
  color: rgb(3, 53, 48);
  margin: 10px 0 4px;
  font-size: clamp(18px, 4vw, 28px); /* shrink for large numbers */
  word-break: break-word;
}

/* ── Hint text (shown after correct answer) ───────────────────────── */
.hint-text {
  font-size: 14px;
  color: rgb(3, 100, 50);
  font-style: italic;
  margin-top: 4px;
}

/* ── Answer buttons ───────────────────────────────────────────────── */
.answer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px;
}
.ans-btn {
  min-height: 56px;
  padding: 10px 12px;
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 2px solid rgb(3, 53, 48);
  border-radius: 8px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  word-break: break-all;
}
.ans-btn:hover:not(:disabled) {
  background: rgb(100, 225, 205);
  transform: scale(1.03);
}
.ans-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

/* ── Next button ─────────────────────────────────────────────────── */
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.next-btn:hover { background-color: rgb(5, 80, 70); }
</style>
